(() => {
  if (window.selenaCalendarLoaded) return;
  window.selenaCalendarLoaded = true;
  const zone = 'Europe/Paris';
  const dayKey = date => {
    const parts = new Intl.DateTimeFormat('en-CA', {timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit'}).formatToParts(date);
    const part = type => parts.find(p => p.type === type).value;
    return `${part('year')}-${part('month')}-${part('day')}`;
  };
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const initialize = root => {
    if (root.dataset.initialized) return;
    const source = root.querySelector('[data-event-source]');
    const data = source?.querySelector('.sl-calendar-data');
    if (!data) return;
    const french = root.dataset.locale?.startsWith('fr');
    const locale = french ? 'fr-FR' : 'en-GB';
    const dateFormat = options => new Intl.DateTimeFormat(locale, {timeZone: zone, ...options});
    const events = Array.from(source.querySelectorAll('[data-cal-event]')).map(node => ({
      id: node.dataset.calEvent,
      kind: node.dataset.kind,
      start: node.dataset.start,
      date: node.dataset.date,
      allDay: node.dataset.allDay === 'true',
      title: french ? node.dataset.titleFr : node.querySelector('h3')?.textContent,
      text: french ? node.dataset.textFr : node.querySelector('p')?.textContent
    })).filter(event => event.date && event.title && !Number.isNaN(Date.parse(event.start)))
      .sort((a, b) => a.date.localeCompare(b.date) || a.start.localeCompare(b.start));
    if (!events.length) return;
    root.dataset.initialized = 'true';
    const labels = {full_moon: root.dataset.labelFull, new_moon: root.dataset.labelNew, seasonal: root.dataset.labelSeasonal};
    const now = new Date();
    const today = dayKey(now);
    const upcoming = events.filter(event => event.allDay ? event.date >= today : new Date(event.start) >= now);
    const list = root.querySelector('[data-event-list]');
    const eventId = event => `${root.id}-${event.id}`;
    const cards = selected => {
      list.replaceChildren();
      if (!selected.length) {
        list.append(element('p', 'sl-cal-empty', root.dataset.labelEmpty));
        return;
      }
      selected.forEach(event => {
        const card = element('article', `sl-cal-card sl-cal-card--${event.kind}`);
        card.id = eventId(event);
        card.tabIndex = -1;
        const mark = element('span', `sl-cal-mark sl-cal-mark--${event.kind}`);
        mark.setAttribute('aria-hidden', 'true');
        const category = element('p', 'sl-cal-category', labels[event.kind]);
        const title = element('h3', '', event.title);
        const date = element('time', 'sl-cal-date', dateFormat({day: 'numeric', month: 'long', year: 'numeric'}).format(new Date(event.start)));
        date.dateTime = event.start;
        const time = event.allDay ? root.dataset.labelTraditional : `${dateFormat({hour: '2-digit', minute: '2-digit', hourCycle: 'h23'}).format(new Date(event.start))} · Paris`;
        card.append(mark, category, title, date, element('p', 'sl-cal-time', time), element('p', 'sl-cal-description', event.text));
        list.append(card);
      });
    };
    if (root.dataset.mode === 'preview') {
      cards(upcoming.slice(0, 3));
      return;
    }
    const monthSelect = root.querySelector('[data-month]');
    const from = (data.dataset.start || events[0].date).slice(0, 7);
    const to = (data.dataset.end || events[events.length - 1].date).slice(0, 7);
    const months = [];
    const [fromYear, fromMonth] = from.split('-').map(Number);
    const [toYear, toMonth] = to.split('-').map(Number);
    for (let cursor = fromYear * 12 + fromMonth - 1; cursor <= toYear * 12 + toMonth - 1; cursor++) {
      const year = Math.floor(cursor / 12);
      const month = cursor % 12;
      const key = `${year}-${String(month + 1).padStart(2, '0')}`;
      months.push(key);
      const option = element('option', '', dateFormat({month: 'long', year: 'numeric'}).format(new Date(Date.UTC(year, month, 1, 12))));
      option.value = key;
      monthSelect.append(option);
    }
    const initialMonth = (upcoming[0]?.date || to).slice(0, 7);
    monthSelect.value = months.includes(initialMonth) ? initialMonth : months[0];
    let filter = 'all';
    const grid = root.querySelector('[data-month-grid]');
    const previous = root.querySelector('[data-previous]');
    const next = root.querySelector('[data-next]');
    const render = () => {
      const selectedMonth = monthSelect.value;
      const index = months.indexOf(selectedMonth);
      previous.disabled = index <= 0;
      next.disabled = index >= months.length - 1;
      const selected = events.filter(event => event.date.startsWith(selectedMonth) && (filter === 'all' || event.kind === filter));
      cards(selected);
      const monthLabel = monthSelect.selectedOptions[0].textContent;
      root.querySelector('[data-list-heading]').textContent = `${monthLabel} · ${selected.length}`;
      grid.setAttribute('aria-label', monthLabel);
      grid.replaceChildren();
      const header = element('div', 'sl-cal-week');
      header.setAttribute('role', 'row');
      for (let day = 0; day < 7; day++) {
        const date = new Date(Date.UTC(2024, 0, 1 + day, 12));
        const heading = element('span', 'sl-cal-weekday', dateFormat({weekday: 'short'}).format(date));
        heading.setAttribute('role', 'columnheader');
        heading.setAttribute('aria-label', dateFormat({weekday: 'long'}).format(date));
        header.append(heading);
      }
      grid.append(header);
      const [year, month] = selectedMonth.split('-').map(Number);
      const offset = (new Date(Date.UTC(year, month - 1, 1)).getUTCDay() + 6) % 7;
      const count = new Date(Date.UTC(year, month, 0)).getUTCDate();
      const total = Math.ceil((offset + count) / 7) * 7;
      let week;
      for (let index = 0; index < total; index++) {
        if (index % 7 === 0) {
          week = element('div', 'sl-cal-week');
          week.setAttribute('role', 'row');
          grid.append(week);
        }
        const cell = element('div', 'sl-cal-day');
        cell.setAttribute('role', 'cell');
        const day = index - offset + 1;
        if (day < 1 || day > count) {
          cell.classList.add('sl-cal-day--blank');
          week.append(cell);
          continue;
        }
        const key = `${selectedMonth}-${String(day).padStart(2, '0')}`;
        const number = element('time', 'sl-cal-number', String(day));
        number.dateTime = key;
        if (key === today) number.setAttribute('aria-current', 'date');
        cell.append(number);
        selected.filter(event => event.date === key).forEach(event => {
          const link = element('a', `sl-cal-chip sl-cal-chip--${event.kind}`);
          link.href = `#${eventId(event)}`;
          link.setAttribute('aria-label', `${event.title} · ${day} ${monthLabel}`);
          link.title = event.title;
          const dot = element('span', `sl-cal-dot sl-cal-dot--${event.kind}`);
          dot.setAttribute('aria-hidden', 'true');
          link.append(dot, element('span', 'sl-cal-chip-name', event.title.split(' · ')[0]));
          cell.append(link);
        });
        week.append(cell);
      }
    };
    previous.addEventListener('click', () => {monthSelect.value = months[months.indexOf(monthSelect.value) - 1]; render();});
    next.addEventListener('click', () => {monthSelect.value = months[months.indexOf(monthSelect.value) + 1]; render();});
    monthSelect.addEventListener('change', render);
    root.querySelector('[data-today]').addEventListener('click', () => {
      monthSelect.value = months.includes(today.slice(0, 7)) ? today.slice(0, 7) : initialMonth;
      render();
    });
    root.querySelectorAll('[data-filter]').forEach(button => button.addEventListener('click', () => {
      filter = button.dataset.filter;
      root.querySelectorAll('[data-filter]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
      render();
    }));
    const checked = root.querySelector('[data-checked]');
    if (checked && data.dataset.updated) {
      checked.dateTime = data.dataset.updated;
      checked.textContent = dateFormat({day: 'numeric', month: 'long', year: 'numeric'}).format(new Date(`${data.dataset.updated}T12:00:00Z`));
    }
    render();
    source.hidden = true;
    root.querySelector('.sl-cal-interface').hidden = false;
  };
  const boot = () => document.querySelectorAll('[data-sl-calendar]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, {once: true});
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
